import type { ExchangeProjectContextBriefDto } from '@inference.engine/api-client';
import { EditorContent } from '@tiptap/react';
import {
  BRIEF_PROSE_CLASSES,
  BRIEF_SECTION_TITLE_CLASSES,
  useBriefSectionViewer,
} from '@/entities/brief';

type BriefSnapshotSectionData = ExchangeProjectContextBriefDto['sections'][number];

// Shares BRIEF_PROSE_CLASSES with the live editor + Library/template previews
// so a snapshotted brief reads identically; read-only needs no tall click target.
const PROSE_OVERRIDES = `${BRIEF_PROSE_CLASSES} [&_.ProseMirror]:min-h-[24px]`;

/**
 * One snapshot section: heading plus a read-only TipTap mount of its content.
 * The viewer returns null only for structurally-malformed content, so a bad
 * section shows its heading with an empty body rather than crashing.
 */
function BriefSnapshotSection(props: { section: BriefSnapshotSectionData }) {
  const editor = useBriefSectionViewer(props.section.content);

  return (
    <section className="flex flex-col gap-2" data-testid="brief-snapshot-section">
      {props.section.name && <h2 className={BRIEF_SECTION_TITLE_CLASSES}>{props.section.name}</h2>}
      {editor && <EditorContent editor={editor} className={PROSE_OVERRIDES} />}
    </section>
  );
}

/**
 * Renders the point-in-time brief snapshot frozen onto a sent message, in the
 * order the sections were sent, using the shared brief prose chrome.
 */
export function BriefSnapshotPreview(props: { brief: ExchangeProjectContextBriefDto }) {
  return (
    <div className="flex flex-col gap-6">
      {props.brief.sections.map((section, index) => (
        // Snapshot sections carry no stable id and the list is static read-only
        // chrome, so the index is a stable key.
        <BriefSnapshotSection key={index} section={section} />
      ))}
    </div>
  );
}
